<template>
  <div :class="'grid gap-6 pt-2 pb-6 overflow-x-scroll w-full' +
  [showManufacturer ? ' grid-cols-5 ' : ' grid-cols-4 ']">
    <FormGroup :label="$t('pages.manufacturers.manufacturers_select_device_revision.vehicle_manufacturer')"
               v-if="showManufacturer">
      <Dropdown
          data-cy="vehicle-manufacturer-option"
          @change="changeCompany"
          filter
          v-model="company_id"
          :options="vehicleManufacturers"
          option-label="name"
          option-value="id"
          :placeholder="$t('pages.manufacturers.manufacturers_select_device_revision.select_vehicle_manufacturer')"
          show-clear
      />
    </FormGroup>
    <FormGroup :label="$t('pages.manufacturers.manufacturers_select_device_revision.device_manufacturer')">
      <Dropdown
          data-cy="device-manufacturer-option"
          @change="changeDeviceManufacturer"
          filter
          v-model="device_manufacturer_id"
          :options="deviceManufacturers"
          option-label="name"
          option-value="id"
          :placeholder="$t('pages.manufacturers.manufacturers_select_device_revision.select_device_manufacturer')"
          show-clear
      />
    </FormGroup>
    <FormGroup :label="$t('pages.manufacturers.manufacturers_select_device_revision.device_type')">
      <Dropdown
          @change="changeDeviceType"
          filter
          v-model="device_type_id"
          :options="deviceTypes"
          option-label="name"
          option-value="id"
          :placeholder="$t('pages.manufacturers.manufacturers_select_device_revision.select_device_type')"
          show-clear
          :disabled="!device_manufacturer_id"
      />
    </FormGroup>
    <FormGroup :label="$t('pages.manufacturers.manufacturers_select_device_revision.device_model')">
      <Dropdown
          data-cy="device-model-option"
          @change="changeDeviceModel"
          filter
          v-model="device_model_id"
          :options="deviceModels"
          option-label="name"
          option-value="id"
          :placeholder="$t('pages.manufacturers.manufacturers_select_device_revision.select_device_model')"
          show-clear
          :disabled="!device_type_id"
      />
    </FormGroup>
    <FormGroup :label="$t('pages.manufacturers.manufacturers_select_device_revision.device_revision')">
      <Dropdown
          data-cy="device-revision-option"
          filter
          @change="changeDeviceRevision"
          v-model="device_revision_id"
          :options="deviceRevisions"
          option-label="name"
          option-value="id"
          :placeholder="$t('pages.manufacturers.manufacturers_select_device_revision.select_device_revision')"
          :disabled="!device_model_id"
      />
    </FormGroup>
  </div>
</template>

<script>
import {useDeviceManufacturersStore} from "@/stores/deviceManufacturers.js";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import FormGroup from "@/Components/FormGroup.vue";
import {mapStores} from "pinia";
import {useCompaniesStore} from "@/stores/companies.js";
import {useForm} from "laravel-precognition-vue";

export default {
  name: "ManufacturersSelectDeviceRevision",
  components: {
    FormGroup
  },
  props: {
    collapsed: {
      type: Boolean,
    },
    showManufacturer: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      options: undefined,
      company_id: null,
      device_manufacturer_id: null,
      device_type_id: null,
      device_model_id: null,
      device_revision_id: null,
      deviceRevision: null,
      indexDeviceManufacturersParams: {},
      indexDeviceTypesParams: {},
      indexCompaniesParams: {
        filter: {
          vehicle_manufacturer: 1
        }
      },
      showDeviceRevisionsParams: {
        include: [
          'deviceType',
          'deviceManufacturer'
        ],
      },
    }
  },
  methods: {
    changeCompany() {
      if (!this.company_id){
        this.$router.push({ name: 'manufacturers' });
      }
      this.device_manufacturer_id = this.getFirst(this.deviceManufacturers)
      this.changeDeviceManufacturer();
    },
    changeDeviceManufacturer() {
      if (!this.showManufacturer) {
        this.goToFirmware()
      }
      this.device_type_id = this.getFirst(this.deviceTypes)
      this.changeDeviceType();
    },
    changeDeviceType() {
      this.device_model_id = this.getFirst(this.deviceModels)
      this.changeDeviceModel();
    },
    changeDeviceModel() {
      this.device_revision_id = this.getFirst(this.deviceRevisions)
    },
    changeDeviceRevision() {
      if (this.showManufacturer) {
        this.$router.push({
          name: 'manufacturers.dashboard',
          params: {
            manufacturer: this.company_id,
            revision: this.device_revision_id
          }
        });
      }
    },
    getFirst(data) {
      if (data.length === 1) return data[0].id
      return undefined;
    },
    goToFirmwareOverview() {
      this.$router.push({
        name: 'firmware.overview',
        params: {
          revision: this.device_revision_id
        }
      });
    },
    goToFirmware() {
      this.$router.push({name: 'firmware'});
    },
  },
  computed: {
    ...mapStores(useCompaniesStore),
    ...mapStores(useDeviceManufacturersStore),
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useDeviceTypesStore),
    vehicleManufacturers() {
      return this.companiesStore.getCompanyOptions?.filter(company => {
        return !!company.vehicle_manufacturer;
      }) || [];
    },
    deviceManufacturers() {
      return this.deviceManufacturersStore.getDeviceManufacturerOptions?.filter(deviceManufacturer => {
        return (deviceManufacturer.company_id === this.company_id || deviceManufacturer.company_id === null)
            && this.options?.some(dT => dT.device_models.some(dM => dM.device_manufacturer_id === deviceManufacturer.id));
      }) || [];
    },
    deviceTypes() {
      return this.options?.map(deviceType => {
        return {
          ...deviceType,
          device_models: deviceType.device_models?.filter(deviceModel => !this.device_manufacturer_id || deviceModel.device_manufacturer_id === this.device_manufacturer_id).map(deviceModel => {
            return {
              ...deviceModel,
              device_revisions: deviceModel.device_revisions?.filter(deviceRevision => {
                return deviceRevision.vehicle_manufacturers?.length === 0 || deviceRevision.vehicle_manufacturers?.some(vm => vm.id === this.company_id);
              })
            }
          }).filter(deviceModel => !!deviceModel.device_revisions.length)
        }
      }).filter(deviceType => !!deviceType.device_models.length);
    },
    deviceModels() {
      return this.deviceTypes?.find(dT => dT.id === this.device_type_id)?.device_models ?? [];
    },
    deviceRevisions() {
      return this.deviceModels?.find(dM => dM.id === this.device_model_id)?.device_revisions ?? [];
    }
  },
  async mounted() {
    await this.companiesStore.indexCompanyOptions(this.indexCompaniesParams);
    if (this.vehicleManufacturers.length === 1) {
      this.company_id = this.vehicleManufacturers[0].id;
    }

    await this.deviceManufacturersStore.indexDeviceManufacturerOptions(this.indexDeviceManufacturersParams);

    this.options = (await this.deviceTypesStore.indexDeviceTypes({
      include: [
        'deviceModels',
        'deviceModels.deviceRevisions',
        'deviceModels.deviceRevisions.vehicleManufacturers'
      ]
    })).data;
  },
  watch: {
    '$route.params': {
      async handler(newValue, oldValue) {
        if (newValue.revision) {
          try {
            this.deviceRevision = await this.deviceRevisionsStore.showDeviceRevision(newValue.revision, this.showDeviceRevisionsParams);

            this.device_manufacturer_id = this.deviceRevision?.device_manufacturer?.id;
            this.device_model_id = this.deviceRevision?.device_model_id;
            this.device_type_id = this.deviceRevision?.device_type?.id;
            this.device_revision_id = this.deviceRevision?.id;
          } catch (e) {
            console.log(e);
          }
        }
        if (newValue.manufacturer) {
          this.company_id = parseInt(newValue.manufacturer);
        }
        else {
          this.company_id = null;
        }
      },
      immediate: true,
    },
    'device_revision_id': {
      async handler(newValue, oldValue) {
        // TODO - I think the manufacturer page will need something like this too
        if (this.showManufacturer) return;
        if (newValue == null) {
            this.goToFirmware();
        } else if (newValue !== oldValue) {
          this.goToFirmwareOverview()
        }
      },
      immediate: true,
    }
  },
}
</script>

<style scoped>

</style>
